import { FC, ReactNode } from "react";
import { FormattedMessage } from "react-intl";

import { Box, Stack, Typography } from "@mui/material";
import type { SxProps, Theme } from "@mui/material";

export const MULTI_ANALYSIS_PANEL_BORDER = "1px solid #d8ddea";

interface IMultiAnalysisPanelProps {
  titleId?: string;
  titleValues?: Record<string, string | number>;
  icon?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  sx?: SxProps<Theme>;
  hideHeader?: boolean;
}

const mergeSx = (baseSx: SxProps<Theme>, sx?: SxProps<Theme>): SxProps<Theme> => {
  if (!sx) {
    return baseSx;
  }

  return [baseSx, ...(Array.isArray(sx) ? sx : [sx])] as SxProps<Theme>;
};

const rootSx: SxProps<Theme> = {
  minHeight: 0,
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  border: MULTI_ANALYSIS_PANEL_BORDER,
  bgcolor: "#ffffff",
  overflow: "hidden",
};

const headerSx: SxProps<Theme> = {
  minHeight: 38,
  px: 1.25,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  borderBottom: MULTI_ANALYSIS_PANEL_BORDER,
  bgcolor: "#f5f7fb",
};

export const MULTI_ANALYSIS_PANEL_COLLAPSED_HEIGHT = 40;

export const getMultiAnalysisExpandablePanelSx = (
  collapsed: boolean,
  expandedMinHeight: number,
  expandedFlexGrow = 1,
): SxProps<Theme> => {
  if (collapsed) {
    return {
      height: MULTI_ANALYSIS_PANEL_COLLAPSED_HEIGHT,
      flex: "0 0 auto",
    };
  }

  return {
    flex: `${expandedFlexGrow} 1 0`,
    minHeight: expandedMinHeight,
  };
};

export const MultiAnalysisPanel: FC<IMultiAnalysisPanelProps> = ({
  titleId,
  titleValues,
  icon,
  actions,
  children,
  sx,
  hideHeader,
}) => (
  <Box sx={mergeSx(rootSx, sx)}>
    {!hideHeader && (titleId || icon || actions) ? (
      <Box sx={headerSx}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
          {icon ? (
            <Box sx={{ color: "#4f5d75", display: "flex", alignItems: "center", flexShrink: 0 }}>{icon}</Box>
          ) : null}
          {titleId ? (
            <Typography sx={{ fontSize: 13, lineHeight: "16px", fontWeight: 600, color: "#363846" }} noWrap>
              <FormattedMessage id={titleId} values={titleValues} />
            </Typography>
          ) : null}
        </Stack>
        {actions ? <Box sx={{ flexShrink: 0 }}>{actions}</Box> : null}
      </Box>
    ) : null}
    <Box sx={{ flex: 1, minHeight: 0, minWidth: 0, overflow: "hidden" }}>{children}</Box>
  </Box>
);
